import { PaginatedQueryResult } from '@query/usePaginatedQuery'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useModal } from '@app/context/ModalsContext'

import { useDateFilterWriteContext } from '@context/DateFilterContext'

import { PageSection } from '@ui/components/PageSection'
import { PercentIcon } from '@ui/icons/Percent.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { SortOptions } from '@ui/v2/Table/Table'
import { DateShortcutOption } from '@ui/v2/TableFilter/Chips/FilterChipModalDateRange'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { useDebouncedValue } from '@utils/debounce'

import { ExportHubPayload } from '@model/ExportHub'
import { FilterQueryProps } from '@model/FilterFields'
import { FilterOptionsDto } from '@model/FilterOptionsDto'
import { NameDto } from '@model/NameDto'

import { useGenerateDocumentFile } from '@hooks/useGenerateDocumentFile'
import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'
import { useScrollToTop } from '@hooks/useScrollToTop'

import { ExportModal } from '@shared/modals/Export.modal'

import {
  last30Days,
  lastMonth,
  lastYear,
  oneWeek,
  thisMonth,
} from '../Dashboard/constants/performance'

import styles from './IBReportResultsPage.module.scss'

const searchInitialQuery = {
  ca_search_TextSearch: undefined,
}

export interface UseSearchProps {
  initialSearchTerm?: string
  customInitialSearchQuery?: { [key: string]: string }
}

export interface SearchResponseProps extends Omit<ReturnType<typeof useSearch>, 'sortOptions'> {
  sortOptions: SortOptions<string>
  placeholder?: string
}

export const useSearch = (props?: UseSearchProps) => {
  const location = useLocation()
  const { initialSearchTerm = null, customInitialSearchQuery } = props ?? {}
  const { clearFilter } = useDateFilterWriteContext()

  const [search, setSearch] = useState<FilterQueryProps>(
    customInitialSearchQuery ?? searchInitialQuery
  )
  const [searchTerm, setSearchTerm] = useState<string | null>(initialSearchTerm)
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500)

  useEffect(() => {
    setSearch(customInitialSearchQuery ?? searchInitialQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const clearSearch = useCallback((callback: () => void) => {
    callback()
    setSearch(customInitialSearchQuery ?? searchInitialQuery)
    clearFilter()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    search,
    setSearch,
    clearSearch,
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    sortOptions: [],
  }
}

const useTradingAccountOptions = () => {
  const { t } = useTranslation()

  const tradingAccountOptions = [
    { name: t('IB.RebatesPage.ClientsPage.TA.Known'), id: 'true' },
    { name: t('IB.RebatesPage.ClientsPage.TA.Not known'), id: 'false' },
  ]

  return { tradingAccountOptions }
}

const useFilterOptions = () => {
  const { t } = useTranslation()

  const calendarRangeOptions: DateShortcutOption[] = [
    {
      name: t('Calendar.Week'),
      range: oneWeek,
    },
    {
      name: t('Calendar.Last 30 Days'),
      range: last30Days,
    },
    {
      name: t('Calendar.This Month'),
      range: thisMonth,
    },
    {
      name: t('Calendar.Last Month'),
      range: lastMonth,
    },
    {
      name: t('Calendar.Last Year'),
      range: lastYear,
    },
  ]

  return { calendarRangeOptions }
}

interface IBReportResultsProps<T> {
  title: string
  scrollToHeaderId: ScrollToIds
  filterOptionsTypes?: FilterOptions[]
  filterOptions?: FilterOptionsDto
  search?: SearchResponseProps
  sortOptions?: NameDto<string>[]
  generateDocumentFile?: ReturnType<typeof useGenerateDocumentFile>
  exportData?: Omit<ExportHubPayload, 'format' | 'mode'>
  data: IAppendableFetchQueryResult<T>
  table: React.ReactNode
  summary?: React.ReactNode
  maxEntriesPerPage?: number
  isPageLoading?: boolean
  onPageChangeStart?: () => void
}

export interface IAppendableFetchQueryResult<T> {
  query: PaginatedQueryResult<T>
}

export const IBReportResultsPage = <T,>(
  props: IBReportResultsProps<T>
): React.ReactElement<any> => {
  const { title, data: reportResults, search, sortOptions, table, maxEntriesPerPage = 100 } = props
  const { scrollToHeaderId, generateDocumentFile, exportData, summary } = props
  const { filterOptions, filterOptionsTypes = [], isPageLoading, onPageChangeStart } = props
  const isFetching = reportResults.query.isFetching || !!isPageLoading
  const hasResults = reportResults.query.hasInitialResults || !!reportResults.query.data.length

  useScrollToTop()

  const { t } = useTranslation()

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const { calendarRangeOptions } = useFilterOptions()
  const { tradingAccountOptions } = useTradingAccountOptions()
  const isFilterButtonDisabled = !filterOptions || !hasResults

  const exportModal = useModal(ExportModal, {
    exportData,
  })

  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.IBClientsHeader,
    isPaginationEntrySelected,
    isFetching,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(ScrollToIds.IBClientsHeader, isFetching, reportResults.query.meta?.pageSize)

  const handleSearchFilter = useCallback(
    (searchFilters?: FilterQueryProps) => {
      if (!searchFilters) return

      search?.setSearch(searchFilters)
      reportResults.query.setPageQuery?.({
        ...reportResults.query.pageQuery,
        pageIndex: 1,
      })
    },
    [search, reportResults.query]
  )

  const handlePageChange = useCallback(
    (pageIndex: number, pageSize?: number) => {
      if (pageSize && pageSize !== reportResults.query.meta?.pageSize) {
        setIsPaginationEntrySelected(true)
      }
      onPageChangeStart?.()
      reportResults.query.setPageQuery?.({
        ...reportResults.query.pageQuery,
        pageIndex,
        pageSize,
      })
    },
    [onPageChangeStart, reportResults.query]
  )

  useEffect(() => {
    if (!(filterOptions && hasResults)) {
      setIsFilterVisible(false)
    }
  }, [filterOptions, hasResults])

  return (
    <div className={styles.container}>
      {summary}
      <div className={styles.pageWrapper}>
        <PageSection
          id={ScrollToIds.IBRebateApprovalsHeader}
          title={title}
          documentFile={generateDocumentFile}
          openExportModal={(exportData && exportModal.open) ?? undefined}
          isFilterButtonDisabled={isFilterButtonDisabled}
          sortOptions={sortOptions ?? []}
          pageQuery={reportResults.query.pageQuery}
          setPageQuery={reportResults.query.setPageQuery}
          {...(filterOptions && {
            onFilterClick: () => setIsFilterVisible((prevState) => !prevState),
          })}
          searchField={
            search && {
              onChange: (v) => {
                search?.setSearchTerm(v)
                reportResults.query.setPageQuery?.({
                  ...reportResults.query.pageQuery,
                  pageIndex: 1,
                })
              },
              value: search.searchTerm,
              disabled: false,
              canClear: true,
              label: search.placeholder,
              renderResult: () => (
                <IBReportResultsModalBody
                  {...props}
                  search={search}
                  onPageChange={handlePageChange}
                  isLoading={isFetching}
                />
              ),
            }
          }
        />
        {filterOptions && hasResults && (
          <TableFilter
            isVisible={isFilterVisible}
            searchFilters={search?.search}
            dateShortcutOptions={calendarRangeOptions}
            filterOptions={filterOptionsTypes}
            customLabels={{
              [FilterOptions.Amount]: t('IB.Commission'),
            }}
            customIcons={{
              [FilterOptions.Amount]: PercentIcon,
            }}
            transactionTypes={filterOptions?.transactionTypes || []}
            transactionStates={filterOptions?.transactionStates || []}
            countryOptions={filterOptions?.countries || []}
            entityOptions={filterOptions?.entities || []}
            tradingAccountTypeOptions={filterOptions?.accountTypes || []}
            tradingAccountOptions={tradingAccountOptions}
            sectionOptions={filterOptions?.sections}
            formatOptions={filterOptions?.formats}
            statusOptions={filterOptions?.statuses}
            setSearchFilters={handleSearchFilter}
            openExportModal={(exportData && exportModal.open) ?? undefined}
          />
        )}
        {table}
        {reportResults.query.meta && hasResults && (
          <Pagination
            scrollToHeaderId={scrollToHeaderId}
            maxPageSize={maxEntriesPerPage}
            pageData={reportResults.query.meta}
            isLoading={isFetching}
            onPageChanged={handlePageChange}
          />
        )}
      </div>
    </div>
  )
}

interface IBResultsModalBodyProps<T> extends IBReportResultsProps<T> {
  search?: SearchResponseProps
  data: IAppendableFetchQueryResult<T>
  isLoading?: boolean
  onPageChange(pageIndex: number, pageSize?: number): void
}

export const IBReportResultsModalBody = <T,>(
  props: IBResultsModalBodyProps<T>
): React.ReactElement<any> => {
  const { scrollToHeaderId, data, table, onPageChange, isLoading } = props

  return (
    <>
      {table}
      {data.query.meta && (
        <Pagination
          scrollToHeaderId={scrollToHeaderId}
          pageData={data.query.meta}
          isLoading={isLoading ?? data.query.isLoading}
          maxPageSize={100}
          onPageChanged={onPageChange}
        />
      )}
    </>
  )
}
